import { useState, useMemo } from "react";
import { Search, Plus, X, Trash2 } from "lucide-react";
import * as api from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { fmt } from "@/lib/format";
import { toast } from "sonner";

function CreateFoodDialog({ onCreate }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ nome: "", base: "100g", tipo: "animale", kcal: "", carbo: "", proteine: "", grassi: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.nome.trim()) { toast.error("Inserisci il nome dell'alimento"); return; }
    try {
      const created = await onCreate(form);
      toast.success(`"${created.nome}" salvato nel database`);
      setForm({ nome: "", base: "100g", tipo: "animale", kcal: "", carbo: "", proteine: "", grassi: "" });
      setOpen(false);
    } catch (e) {
      toast.error("Errore durante il salvataggio");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="flex w-full items-center gap-2 border-t border-white/5 px-3 py-2.5 text-sm font-medium text-primary transition-colors hover:bg-primary/10" data-testid="open-create-food-btn">
          <Plus className="h-4 w-4" /> Crea nuovo alimento
        </button>
      </DialogTrigger>
      <DialogContent className="border-white/10 bg-[hsl(var(--card))] text-foreground sm:max-w-md" data-testid="create-food-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Nuovo Alimento (valori {form.base === "pezzo" ? "per pezzo" : "per 100g"})</DialogTitle>
          <DialogDescription>Scegli l'unità di riferimento e inserisci i valori nutrizionali {form.base === "pezzo" ? "per singolo pezzo" : "per 100 g"}. L'alimento sarà salvato nel database condiviso.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <div className="field-label mb-1">Nome</div>
            <input className="lab-input" value={form.nome} onChange={(e) => set("nome", e.target.value)} placeholder="es. Crema di Arachidi" data-testid="new-food-nome" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="field-label mb-1">Quantità riferita a</div>
              <div className="flex gap-1 rounded-lg border border-white/10 bg-black/25 p-1">
                {[["100g", "Su 100 g"], ["pezzo", "Pezzi"]].map(([v, l]) => (
                  <button key={v} onClick={() => set("base", v)} data-testid={`new-food-base-${v}`}
                    className={`flex-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${form.base === v ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>{l}</button>
                ))}
              </div>
            </div>
            <div>
              <div className="field-label mb-1">Tipo proteina</div>
              <div className="flex gap-1 rounded-lg border border-white/10 bg-black/25 p-1">
                {[["animale", "Animale"], ["veg", "Vegetale"]].map(([v, l]) => (
                  <button key={v} onClick={() => set("tipo", v)} data-testid={`new-food-tipo-${v}`}
                    className={`flex-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${form.tipo === v ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>{l}</button>
                ))}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[["kcal", "Kcal"], ["carbo", "Carbo (g)"], ["proteine", "Proteine (g)"], ["grassi", "Grassi (g)"]].map(([k, l]) => (
              <div key={k}>
                <div className="field-label mb-1">{l}</div>
                <input type="number" className="lab-input font-mono-num" value={form[k]} onChange={(e) => set(k, e.target.value)} data-testid={`new-food-${k}`} />
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <button onClick={submit} className="w-full rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5" data-testid="save-new-food-btn">
            Salva nel Database
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AddFoodPopover({ foods, onCreate, onAdd }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [qty, setQty] = useState(100);

  const isPezzo = selected?.base === "pezzo";
  const factor = isPezzo ? (Number(qty) || 0) : (Number(qty) || 0) / 100;

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return foods.filter((f) => !s || f.nome.toLowerCase().includes(s) || (f.categoria || "").toLowerCase().includes(s));
  }, [foods, q]);

  const removeFood = async (e, f) => {
    e.stopPropagation();
    if (!window.confirm(`Eliminare "${f.nome}" dal database alimenti?`)) return;
    try {
      await api.deleteFood(f.id);
      window.dispatchEvent(new Event("foods-changed"));
      toast.success(`"${f.nome}" eliminato dal database`);
    } catch (err) { toast.error("Errore durante l'eliminazione"); }
  };

  const pick = (f) => { setSelected(f); setQty(f.base === "pezzo" ? 1 : 100); };

  const confirmAdd = () => {
    if (!selected || (Number(qty) || 0) <= 0) return;
    onAdd(selected, qty);
    setSelected(null);
    setQty(100);
    setQ("");
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setSelected(null); setQ(""); } }}>
      <PopoverTrigger asChild>
        <button className="inline-flex items-center gap-1.5 rounded-lg border border-primary/40 bg-primary/10 px-3 py-1.5 text-xs font-semibold text-primary transition-colors hover:bg-primary/20" data-testid="add-food-btn">
          <Plus className="h-3.5 w-3.5" /> Alimento
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 overflow-hidden border-white/10 bg-[hsl(var(--popover))] p-0 text-foreground" data-testid="food-popover">
        {!selected ? (
          <>
            <div className="flex items-center gap-2 border-b border-white/5 px-3 py-2.5">
              <Search className="h-4 w-4 text-muted-foreground" />
              <input autoFocus className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground/60" placeholder="Cerca alimento..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="food-search-input" />
            </div>
            <div className="max-h-64 overflow-y-auto">
              {filtered.length === 0 && <div className="px-3 py-6 text-center text-xs text-muted-foreground">Nessun alimento trovato</div>}
              {filtered.map((f) => (
                <div key={f.id} role="button" tabIndex={0} onClick={() => pick(f)} onKeyDown={(e) => e.key === "Enter" && pick(f)} className="flex w-full cursor-pointer items-center justify-between gap-2 px-3 py-2 text-left transition-colors hover:bg-white/5" data-testid={`food-item-${f.id}`}>
                  <div className="min-w-0">
                    <div className="truncate text-sm">{f.nome}</div>
                    <div className="text-[11px] text-muted-foreground">{f.categoria} · {fmt(f.kcal)} kcal/{f.base === "pezzo" ? "pezzo" : "100g"}</div>
                  </div>
                  <span className={`ml-auto shrink-0 rounded px-1.5 py-0.5 text-[9px] font-bold uppercase ${f.tipo === "animale" ? "bg-[hsl(var(--c-prot))]/15 text-[hsl(var(--c-prot))]" : "bg-[hsl(var(--c-carb))]/15 text-[hsl(var(--c-carb))]"}`}>
                    {f.tipo === "animale" ? "Animale" : "Veg"}
                  </span>
                  <button type="button" onClick={(e) => removeFood(e, f)} title="Elimina dal database" className="shrink-0 rounded p-1 text-muted-foreground transition-colors hover:bg-destructive/15 hover:text-destructive" data-testid={`delete-food-${f.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
            <CreateFoodDialog onCreate={onCreate} />
          </>
        ) : (
          <div className="p-3" data-testid="food-grams-panel">
            <div className="mb-3 flex items-start justify-between gap-2">
              <div>
                <div className="text-sm font-semibold">{selected.nome}</div>
                <div className="text-[11px] text-muted-foreground">{selected.categoria} · {fmt(selected.kcal)} kcal/{isPezzo ? "pezzo" : "100g"}</div>
              </div>
              <button onClick={() => setSelected(null)} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
            </div>
            <div className="field-label mb-1">{isPezzo ? "N° pezzi" : "Quantità (g)"}</div>
            <input type="number" autoFocus className="lab-input font-mono-num" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="food-grams-input" />
            <div className="mt-2 grid grid-cols-4 gap-1 text-center text-[11px]">
              <div><div className="text-muted-foreground">Kcal</div><div className="font-mono-num text-[hsl(var(--c-kcal))]">{fmt(selected.kcal * factor)}</div></div>
              <div><div className="text-muted-foreground">Carbo</div><div className="font-mono-num text-[hsl(var(--c-carb))]">{fmt(selected.carbo * factor, 1)}</div></div>
              <div><div className="text-muted-foreground">Prot</div><div className="font-mono-num text-[hsl(var(--c-prot))]">{fmt(selected.proteine * factor, 1)}</div></div>
              <div><div className="text-muted-foreground">Grassi</div><div className="font-mono-num text-[hsl(var(--c-fat))]">{fmt(selected.grassi * factor, 1)}</div></div>
            </div>
            <button onClick={confirmAdd} className="mt-3 w-full rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground" data-testid="confirm-add-food-btn">Aggiungi al pasto</button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
